import React, { useState } from "react";
import { X, Calendar, Activity, Clock, Trash2 } from "lucide-react";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAppContext } from "../context/AppContext";

export default function MedicalInfoModal({ setShowInfo, activePet, activePetId }: { setShowInfo: (v: boolean) => void, activePet: any, activePetId: string }) {
  const { t, lang, user } = useAppContext();
  const [infoTab, setInfoTab] = useState<"diagnoses" | "meds" | "allergies" | "cycle">("diagnoses");

  const handleRemoveMedicalTag = async (category: "diagnoses" | "meds" | "allergies", item: string) => {
    if (!user || !activePetId) return;
    await setDoc(doc(db, "users", user.uid, "pets", String(activePetId)), {
      [category]: (activePet[category] || []).filter((x: string) => x !== item),
    }, { merge: true });
  };

  // 🚀 Функція видалення випадкового запису циклу тічки
  const handleDeleteCycle = async (indexToDelete: number) => {
    if (!user || !activePetId) return;
    if (!window.confirm(lang === 'uk' ? "Видалити цей запис тічки?" : "Delete this heat record?")) return;

    const updatedHistory = [...(activePet.heatHistory || [])];
    updatedHistory.splice(indexToDelete, 1);

    await setDoc(doc(db, "users", user.uid, "pets", String(activePetId)), {
      heatHistory: updatedHistory,
    }, { merge: true });
  };

  const isFemale = activePet.gender === t.girl || activePet.gender === "Дівчинка" || activePet.gender === "Girl";

  const calculateNextHeat = () => {
    const history = activePet.heatHistory || [];
    if (history.length < 2) return null;

    let totalDays = 0;
    for (let i = 1; i < history.length; i++) {
      const prevStart = new Date(history[i - 1].start).getTime();
      const currStart = new Date(history[i].start).getTime();
      totalDays += (currStart - prevStart) / (1000 * 60 * 60 * 24);
    }
    
    const avgCycle = Math.round(totalDays / (history.length - 1));
    const lastHeat = new Date(history[history.length - 1].start);
    lastHeat.setDate(lastHeat.getDate() + avgCycle);
    
    return {
      avgCycle,
      nextDate: lastHeat.toLocaleDateString(lang === 'uk' ? 'uk-UA' : 'en-US', { day: 'numeric', month: 'long', year: 'numeric' })
    };
  };

  const cycleData = calculateNextHeat();

  const tabs = [
    { id: "diagnoses", label: t.modDiag },
    { id: "meds", label: t.modMeds },
    { id: "allergies", label: t.modAllergies },
  ];
  
  if (isFemale && !activePet.isSterilized) {
    tabs.push({ id: "cycle", label: lang === 'uk' ? "Тічка" : "Cycle" });
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex flex-col justify-end backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-t-[2.5rem] p-6 pb-12 border-t border-[var(--border-color)] animate-slide-up">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-extrabold text-xl text-[var(--text-main)]">{t.modFeatures}</h3>
          <button onClick={() => setShowInfo(false)} className="text-[var(--text-main)] active:scale-95"><X size={20} /></button>
        </div>
        
        <div className="mb-4">
          <div className="flex overflow-x-auto no-scrollbar bg-[var(--bg-soft)] p-1 rounded-xl mb-4 border border-[var(--border-color)]">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setInfoTab(tab.id as any)} className={`shrink-0 flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all ${infoTab === tab.id ? "bg-[var(--bg-card)] text-[#a073e7] shadow-sm" : "text-[var(--text-muted)]"}`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="min-h-[120px] max-h-[40vh] overflow-y-auto no-scrollbar">
            {infoTab === "cycle" ? (
              <div className="flex flex-col gap-3 animate-fade-in">
                {activePet.isHeatActive ? (
                  <div className="p-3 bg-[#ef4444] bg-opacity-10 border border-[#ef4444] border-opacity-30 rounded-xl flex items-center gap-3">
                    <Activity className="text-[#ef4444] animate-pulse shrink-0" size={24} />
                    <div>
                      <p className="text-[#ef4444] font-black text-sm">{lang === 'uk' ? 'Тічка активна зараз' : 'Heat is active now'}</p>
                      <p className="text-xs text-[var(--text-main)] mt-0.5">Початок: {new Date(activePet.heatStartDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                ) : cycleData ? (
                  <div className="p-3 bg-[var(--accent-soft)] border border-[#a073e7] border-opacity-40 rounded-xl flex items-center gap-3">
                    <Calendar className="text-[#a073e7] shrink-0" size={24} />
                    <div>
                      <p className="text-[#a073e7] font-black text-sm">{lang === 'uk' ? 'Наступна тічка:' : 'Next cycle expected:'}</p>
                      <p className="text-sm font-bold text-[var(--text-main)]">{cycleData.nextDate}</p>
                      <p className="text-[10px] text-[var(--text-muted)] mt-0.5 font-bold uppercase tracking-wide">Середній інтервал: {cycleData.avgCycle} днів</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-[var(--bg-soft)] border border-[var(--border-color)] rounded-xl flex items-center gap-3">
                    <Clock className="text-[var(--text-muted)] shrink-0" size={24} />
                    <div>
                      <p className="text-[var(--text-main)] font-black text-sm">{lang === 'uk' ? 'Недостатньо даних' : 'Not enough data'}</p>
                      <p className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-snug">Запишіть мінімум 2 цикли, щоб алгоритм вирахував дату наступної тічки.</p>
                    </div>
                  </div>
                )}

                <h4 className="font-bold text-xs text-[var(--text-muted)] uppercase tracking-wide mt-2">{lang === 'uk' ? 'Історія циклів' : 'Cycle History'}</h4>
                {(!activePet.heatHistory || activePet.heatHistory.length === 0) && (
                  <p className="text-sm text-[var(--text-muted)] font-bold text-center py-4">{lang === 'uk' ? 'Немає записів.' : 'No records.'}</p>
                )}
                <div className="flex flex-col gap-2">
                  {/* 🚀 Виводимо історію і додаємо кнопку видалення */}
                  {activePet.heatHistory?.slice().reverse().map((record: any, idx: number) => {
                    const originalIndex = activePet.heatHistory.length - 1 - idx;
                    return (
                      <div key={idx} className="flex justify-between items-center bg-[var(--bg-card)] border border-[var(--border-color)] px-4 py-3 rounded-xl shadow-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-sm text-[var(--text-main)]">{new Date(record.start).toLocaleDateString(lang === 'uk' ? 'uk-UA' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          <span className="text-[var(--text-muted)] text-xs font-black">—</span>
                          <span className="font-bold text-sm text-[var(--text-main)]">{new Date(record.end).toLocaleDateString(lang === 'uk' ? 'uk-UA' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                        <button 
                          onClick={() => handleDeleteCycle(originalIndex)} 
                          className="text-[var(--text-muted)] hover:text-[var(--red-text)] active:scale-95 transition-all p-1"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 animate-fade-in">
                {(!activePet[infoTab] || activePet[infoTab]?.length === 0) && <p className="text-sm text-[var(--text-muted)] text-center font-bold py-4 w-full">{t.noData}</p>}
                {(activePet[infoTab] || []).map((item: string) => (
                  <span key={item} className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--accent-soft)] text-[#a073e7] text-xs font-bold rounded-xl border border-[var(--border-accent)]">
                    {item} <button onClick={() => handleRemoveMedicalTag(infoTab, item)} className="hover:text-[var(--red-text)] active:scale-95"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <button onClick={() => setShowInfo(false)} className="w-full mt-2 py-4 bg-[var(--bg-card)] border-2 border-[var(--border-color)] text-[var(--text-main)] font-bold rounded-xl shadow-sm text-sm active:scale-95 transition-all">{t.close}</button>
      </div>
    </div>
  );
}